'use client';

import { FunctionComponent, ReactNode, Ref, forwardRef } from 'react';
import classNames from 'classnames';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';

// Типы кнопок
export enum ButtonType {
  FLAT = 'flat',
  STROKED = 'stroked',
  BASIC = 'basic',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum ButtonSize {
  XL = 'xl',
  /**
   * Just like XL, but with smaller horizontal padding
   */
  XL2 = 'xl2',
  MD = 'md',
  SM = 'sm',
  CUSTOM = 'custom',
}

interface CommonProps {
  text: string;
  bg: string;
  overlay: string;
  radius: string;
  shadow: string;
  border: string;
}

export interface ButtonProps extends Partial<Pick<CommonProps, 'text' | 'bg' | 'shadow' | 'border' | 'radius'>> {
  'type': ButtonType;
  'onClick'?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  'children': ReactNode;
  'disabled'?: boolean;
  'isIcon'?: boolean;
  'size'?: ButtonSize;
  'textFlat'?: string;
  'isFull'?: boolean;
  'className'?: string;
  'data-cy'?: string;
  'themeColors'?: ThemeColorSchemas;
  'themeStyle'?: PortalStyleConfig;
  'ref'?: Ref<HTMLButtonElement>;
  'disabledClassName'?: string;
  'borderDisabled'?: string;
}

export const buttonSize = {
  [ButtonSize.XL]: 'h-9 px-8',
  [ButtonSize.XL2]: 'h-9 px-3',
  [ButtonSize.MD]: 'h-8 px-8',
  [ButtonSize.SM]: 'h-6 px-6',
  [ButtonSize.CUSTOM]: '',
};

export const iconButtonSize = {
  [ButtonSize.XL]: 'h-10 w-10',
  [ButtonSize.XL2]: 'h-9 w-9',
  [ButtonSize.MD]: 'h-8 w-8',
  [ButtonSize.SM]: 'h-6 w-6',
  [ButtonSize.CUSTOM]: '',
};

export const Button: FunctionComponent<ButtonProps> = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type,
      children,
      onClick,
      disabled,
      bg = 'bg-cyan-900 hover:bg-cyan-700 active:bg-cyan-700',
      shadow = '',
      textFlat = 'text-white',
      text = 'text-cyan-900 hover:text-cyan-700 active:text-cyan-700',
      border = 'border border-cyan-900 hover:border-cyan-700 active:border-cyan-700',
      borderDisabled = 'border border-gray-400',
      radius = 'rounded-md',
      isIcon,
      size = ButtonSize.XL,
      isFull,
      'className': externalClassName,
      'data-cy': dataCy,
      themeColors,
      themeStyle,
      disabledClassName,
    },
    ref
  ) => {
    const bgButton = themeColors
      ? classNames([
          ...getBgClassNames(themeColors, PortalBgType.SECONDARY),
          ...getBgClassNames(themeColors, PortalBgType.SECONDARY_HOVER, [PortalClassState.HOVER]),
          ...getBgClassNames(themeColors, PortalBgType.SECONDARY_HOVER, [PortalClassState.ACTIVE]),
        ])
      : bg;

    const textButton = themeColors
      ? classNames([
          ...getTextClassNames(themeColors, PortalTextType.PRIMARY),
          ...getTextClassNames(themeColors, PortalTextType.PRIMARY_HOVER, [PortalClassState.HOVER]),
          ...getTextClassNames(themeColors, PortalTextType.PRIMARY_HOVER, [PortalClassState.ACTIVE]),
        ])
      : text;

    const textButtonPrimary = themeColors
      ? classNames([...getTextClassNames(themeColors, PortalTextType.SECONDARY)])
      : 'text-white';

    const borderButton =
      themeColors && themeStyle
        ? disabled
          ? borderDisabled
          : classNames([
              ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY),
              ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY, {
                states: [PortalClassState.HOVER],
              }),
            ])
        : !disabled
        ? border
        : borderDisabled;
    const getButtonClass = () => {
      switch (type) {
        case ButtonType.FLAT:
          return `${!disabled ? bgButton : disabledClassName || 'bg-gray-200'} ${
            !disabled ? textFlat : 'text-gray-400'
          } ${shadow} ${radius}`;
        case ButtonType.PRIMARY:
          return classNames(shadow, radius, {
            [bgButton]: !disabled,
            [textButtonPrimary]: !disabled,
            'bg-gray-200 text-gray-400': disabled,
          });
        case ButtonType.STROKED:
          return `${!disabled ? textButton : 'text-gray-400'} ${borderButton} ${shadow} ${radius}`;
        case ButtonType.BASIC:
          return `${!disabled ? textButton : 'text-gray-400'}`;
        case ButtonType.SECONDARY:
          bg = 'bg-[#ffffff] hover:bg-neutral-100';
          text = 'text-neutral-500';
          border = 'border border-neutral-200';
          radius = 'rounded';
          return `${!disabled ? text : 'text-neutral-300'} ${bg} ${border} ${radius}`;
        default:
          return '';
      }
    };

    const getButtonSize = (size: ButtonSize, isIcon: boolean | undefined) => {
      return isIcon ? iconButtonSize[size] : buttonSize[size];
    };

    const buttonClasses = classNames(
      'flex items-center justify-center whitespace-nowrap font-bold text-sm select-none',
      getButtonSize(size, isIcon),
      getButtonClass(),
      { 'w-full': isFull },
      externalClassName
    );

    return (
      <button
        data-cy={dataCy ? dataCy : `button-type-${type}`}
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled}
        ref={ref}
      >
        {children}
      </button>
    );
  }
);

export function getButtonSizeFromString(size: string): ButtonSize {
  switch (size) {
    case 'xl':
      return ButtonSize.XL;
    case 'md':
      return ButtonSize.MD;
    case 'sm':
      return ButtonSize.SM;
    default:
      return ButtonSize.SM;
  }
}
